<template>
  <div class="cart-freebie-card" :class="{ [$class('loading')]: !product }">
    <label class="cart-freebie-card__card" :for="name">
      <template v-if="showCheckbox">
        <input
          :id="name"
          :checked="selected"
          class="cart-freebie-card__input"
          :disabled="disabled"
          :name="name"
          type="checkbox"
          @input.prevent.stop="handleInputChange"
        />
        <span
          class="cart-freebie-card__checkmark cart-freebie-card__checkbox"
          aria-hidden="true"
        >
          <icon-check-outline class="cart-freebie-card__check" />
        </span>
      </template>
      <span
        v-if="product && !variant?.available"
        class="cart-freebie-card__sold-out text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
        v-html="availableString"
      />

      <div class="cart-freebie-card__image-container">
        <responsive-image
          class="cart-freebie-card__image"
          :image="featuredImage"
          ratio="1:1"
          sizes="44px"
          widths="44, 106, 159"
        />
      </div>

      <div class="cart-freebie-card__details">
        <p
          class="cart-freebie-card__title text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
          v-text="product?.title"
        />

        <p
          v-if="!product.hasOnlyDefaultVariant"
          class="cart-freebie-card__subtitle text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          v-text="variant?.title"
        />
      </div>
      <quick-add
        v-if="!showCheckbox"
        class="cart-freebie-card__quick-add"
        :product="product"
        :variant="variant"
        :freebie-handle="freebieHandle"
        :show-page-toast="false"
        show-cart-toast
      />
    </label>
  </div>
</template>

<script>
/**
 * Vue: Cart freebie card (cart-freebies)
 * -----------------------------------------------------------------------------
 * Single variant tile for cart freebies.
 *
 * @param {String} name - Form input name.
 * @param {Boolean|Object} product - Product to display.
 * @param {Number} queueLimit - Queue limit.
 * @param {Number} queueSize - Queue size.
 * @param {Boolean} [selected] - Pre-select checkbox.
 * @param {Boolean} [showCheckbox] - Display checkbox.
 *
 * @emits queueVariant - Add variant ID to queue.
 *
 */
import ResponsiveImage from '~global/images/responsive-image';
import QuickAdd from '~async/quick-add/quick-add';

import IconCheckOutline from '~icons/general/check-outline.svg';

export default {
  name: 'CartFreebieCard',

  components: {
    IconCheckOutline,
    ResponsiveImage,
    QuickAdd,
  },

  provide() {
    return {
      variantsShowQatcOverlay: false,
    };
  },

  props: {
    name: {
      type: String,
      default: '',
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    queueLimit: {
      type: Number,
      default: 1,
      required: true,
    },
    queueSize: {
      type: Number,
      default: 0,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    freebieHandle: {
      type: [Boolean, String],
      default: false,
    },
  },

  emits: {
    'select-product': (payload) => typeof payload === 'number',
  },

  computed: {
    /**
     * Compute featured image.
     */
    featuredImage() {
      if (this.variant) {
        if (this.variant.localizedImage) {
          return this.variant.localizedImage;
        }

        return this.variant.featuredImage;
      }

      return this.product.featuredImage;
    },

    /**

     * Compute available string.
     * @returns {String}
     */
    availableString() {
      if (this.variant.available) {
        return '';
      }

      if (this.variant.availableState === 'back-soon') {
        return this.$string('product.badges.back_soon_html');
      }

      return this.$string('product.badges.sold_out_html');
    },

    /**
     * Compute if the card should be disabled.
     * only disables when there are several items selectable
     * @returns {Boolean}
     */
    disabled() {
      return (
        !this.product ||
        !this.variant.available ||
        (this.queueSize >= this.queueLimit &&
          !this.selected &&
          this.queueLimit !== 1)
      );
    },

    variant() {
      return this.product && this.product.selectedOrFirstAvailableVariant;
    },
  },

  methods: {
    /**
     * Handle input change.
     * when there is only one freebie allowed to ensure it only allows
     * to add one freebie at a time. This is why the emit for queue variant
     * on input change
     */
    handleInputChange(event) {
      if (
        !this.selected &&
        this.queueLimit > 1 &&
        this.queueLimit === this.queueSize
      ) {
        event.target.checked = false;
        return false;
      }
      this.$emit('select-product', this.variant.id, !this.selected);

      return true;
    },
  },
};
</script>

<style lang="scss">
@import './cart-freebie-card';
</style>
